<template>
    <v-dialog v-model="dialog" width="500" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card flat class="d-flex flex-column">
            <v-card-title class="pb-0">
                <v-spacer />

                <v-btn icon color="primary" @click="$emit('input', false)">
                    <v-icon>fas fa-times</v-icon>
                </v-btn>
            </v-card-title>

            <template v-if="contact">
                <v-card-title class="pt-0">
                    <p class="text-h5">{{ contact.firstname }} {{ contact.lastname }}</p>
                </v-card-title>

                <v-card-text>
                    <p v-if="age">{{ age }} {{ $t('years old') }}</p>

                    <p v-if="contact.phone" class="d-flex align-end">
                        <a class="primary--text font-weight-bold" :href="`tel:${contact.phone}`" target="_blank">
                            <span>{{ contact.phone }}</span>
                        </a>
                        <a :href="`https://wa.me/${contact.phone}`" target="_blank">
                            <v-icon color="primary" class="ml-2">fab fa-whatsapp</v-icon>
                        </a>
                    </p>

                    <p>
                        <a class="primary--text font-weight-bold" :href="'mailto:' + contact.email">{{
                            contact.email
                        }}</a>
                    </p>

                    <v-card outlined flat>
                        <v-card-title rounded>
                            <template v-if="isAdmin">
                                {{ $t('Admin') }}
                            </template>
                            <template v-else>
                                {{ $t('Not Admin') }}
                            </template>

                            <v-spacer></v-spacer>

                            <v-btn color="primary" depressed @click="updateRole" :loading="roleUpdating">
                                <template v-if="isAdmin">
                                    {{ $t('Downgrade to participant') }}
                                </template>
                                <template v-else>
                                    {{ $t('Make Admin') }}
                                </template>
                            </v-btn>
                        </v-card-title>
                    </v-card>

                    <v-alert type="error" dismissible v-model="alert">
                        {{ $t(error) }}
                    </v-alert>
                </v-card-text>

                <v-spacer />

                <v-card-actions>
                    <v-btn
                        large
                        block
                        text
                        color="primary"
                        @click="deleteConfirmationModal = true"
                        v-if="false === hideRemoveContactButton"
                    >
                        <v-icon class="mr-2">far fa-trash-alt</v-icon>
                        {{ $t('Delete contact') }}
                    </v-btn>
                </v-card-actions>
            </template>

            <template v-else>
                <v-card-title>
                    <v-skeleton-loader type="heading" width="50%"></v-skeleton-loader>
                </v-card-title>

                <v-card-text>
                    <v-skeleton-loader type="text" width="30%" class="mt-3"></v-skeleton-loader>

                    <v-skeleton-loader type="text" width="40%" class="mt-3"></v-skeleton-loader>

                    <v-skeleton-loader type="text" width="50%" class="mt-3"></v-skeleton-loader>

                    <v-row>
                        <v-col xs="6">
                            <v-skeleton-loader type="heading" width="300"></v-skeleton-loader>
                        </v-col>

                        <v-col xs="6" class="text-right">
                            <v-skeleton-loader class="d-inline-block" type="button"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-row>
                        <v-col xs="12" class="text-center">
                            <v-skeleton-loader
                                class="d-inline-block"
                                type="button"
                                v-if="false === hideRemoveContactButton"
                            ></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </template>
        </v-card>

        <v-dialog v-model="deleteConfirmationModal" width="500" persistent>
            <v-card>
                <v-card-title>
                    <span class="subtitle">{{$t('Delete contact?')}}</span>

                    <v-spacer />

                    <v-btn icon color="primary" @click="deleteConfirmationModal = false">
                        <v-icon>fas fa-times</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    {{$t('Do you really wanna delete this contact?')}}
                </v-card-text>

                <v-card-actions>
                    <v-row class="ma-0">
                        <v-col xs="6">
                            <v-btn block color="gray" :loading="contactDeleting" @click="deleteContact">
                                {{$t('Delete')}}
                            </v-btn>
                        </v-col>

                        <v-col xs="6">
                            <v-btn
                                block
                                color="primary"
                                @click="deleteConfirmationModal = false"
                                :disabled="contactDeleting"
                            >
                                {{$t('Cancel')}}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<style scoped>
a {
    text-decoration: none;
}
</style>

<script>
import { ParticipantModel, ParticipantOrganizationModel } from '../models';
import moment from 'moment';
import { mapState } from 'vuex';

export default {
    props: {
        contactId: null,
        value: null,
        hideRemoveContactButton: {
            type: Boolean,
        },
    },
    data() {
        return {
            contact: null,
            roleUpdating: false,
            contactDeleting: false,
            error: '',
            alert: false,
            deleteConfirmationModal: false,
        };
    },
    created() {
        ParticipantModel.getOne(this.contactId, {
            params: {
                'response-group': 'participants_list',
            },
        })
            .then(result => (this.contact = result))
            .catch(e => {
                this.error = 'Cannot load contact. ' + e.toString();
                this.alert = true;
            });
    },
    computed: {
        ...mapState('organization', ['selectedOrganizationID']),
        age() {
            if (this.contact.birthdate) {
                let birthdate = new moment(this.contact.birthdate.date);
                let today = new moment();
                let duration = moment.duration(today.diff(birthdate));

                return parseInt(duration.as('years'));
            }
        },
        isAdmin() {
            return this.participantOrganization ? this.participantOrganization.role === 'Admin' : false;
        },
        participantOrganization() {
            let result = null;

            if (this.contact.participantOrganizations) {
                this.contact.participantOrganizations.some(participantOrganization => {
                    if (
                        participantOrganization.organization &&
                        participantOrganization.organization.id === this.selectedOrganizationID
                    ) {
                        return (result = participantOrganization);
                    }
                });
            }

            return result;
        },
        dialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
    },
    methods: {
        updateRole() {
            this.roleUpdating = true;

            ParticipantOrganizationModel.patch(this.participantOrganization.id, {
                params: {
                    'response-group': 'default',
                },
                body: {
                    role: this.participantOrganization.role === 'Admin' ? null : 'Admin',
                },
            })
                .then(r => {
                    this.participantOrganization.role = r.role;
                    this.$emit('role-change', r.role);
                })
                .catch(e => {
                    this.error = 'Cannot update role! ' + e.toString();
                    this.alert = true;
                })
                .finally(() => (this.roleUpdating = false));
        },
        deleteContact() {
            this.contactDeleting = true;

            ParticipantOrganizationModel.delete(this.participantOrganization.id)
                .then(r => {
                    this.dialog = false;
                    this.$emit('removed');
                })
                .catch(e => {
                    this.error = 'Cannot delete contact! ' + e.toString();
                    this.alert = true;
                })
                .finally(() => {
                    this.deleteConfirmationModal = false;
                    this.contactDeleting = false;
                });
        },
    },
};
</script>
